'use client'

import React, { useState } from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { Check, ChevronDown, Search, X } from 'lucide-react'
import { BaseFieldProps } from '../types'
import { cn, baseInputStyles, inputVariants, labelStyles, errorStyles, helperTextStyles, buttonStyles, buttonVariants } from '../utils/styles'

export type GenderOption = {
  value: string
  label: string
}

export const GENDER_OPTIONS: GenderOption[] = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'non-binary', label: 'Non-binary' },
  { value: 'genderfluid', label: 'Genderfluid' },
  { value: 'agender', label: 'Agender' },
  { value: 'demigender', label: 'Demigender' },
  { value: 'bigender', label: 'Bigender' },
  { value: 'pangender', label: 'Pangender' },
  { value: 'genderqueer', label: 'Genderqueer' },
  { value: 'two-spirit', label: 'Two-Spirit' },
  { value: 'transgender', label: 'Transgender' },
  { value: 'questioning', label: 'Questioning' },
  { value: 'other', label: 'Other' },
  { value: 'prefer-not-to-say', label: 'Prefer not to say' },
]

export interface GenderSelectorProps extends BaseFieldProps {
  value?: string | string[]
  onChange: (value: string | string[]) => void
  multiple?: boolean
  placeholder?: string
  searchPlaceholder?: string
  allowCustom?: boolean
  maxSelections?: number
  showClearButton?: boolean
}

// Checkbox component for consistency
const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      'peer h-4 w-4 shrink-0 rounded-sm border border-gray-300 ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white',
      className
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
      <Check className="h-4 w-4" />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
))
Checkbox.displayName = CheckboxPrimitive.Root.displayName

// Input component for consistency
const Input = React.forwardRef<
  HTMLInputElement,
  React.ComponentPropsWithoutRef<'input'>
>(({ className, ...props }, ref) => (
  <input
    ref={ref}
    className={cn(
      baseInputStyles,
      inputVariants.default,
      className
    )}
    {...props}
  />
))
Input.displayName = 'Input'

// Badge component for selected items
const Badge: React.FC<{
  children: React.ReactNode
  onRemove?: () => void
  className?: string
}> = ({ children, onRemove, className }) => (
  <div className={cn('inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full', className)}>
    <span>{children}</span>
    {onRemove && (
      <button
        type="button"
        onClick={onRemove}
        className="ml-1 hover:text-blue-600 focus:outline-none"
        aria-label={`Remove ${children}`}
      >
        <X className="h-3 w-3" />
      </button>
    )}
  </div>
)

export const GenderSelector: React.FC<GenderSelectorProps> = ({
  value,
  onChange,
  multiple = false,
  placeholder = 'Select gender...',
  searchPlaceholder = 'Search genders...',
  allowCustom = false,
  maxSelections = 10,
  showClearButton = true,
  error,
  label,
  helperText,
  disabled = false,
  required = false,
  className,
  ...props
}) => {
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [customValue, setCustomValue] = useState('')
  const [showCustomInput, setShowCustomInput] = useState(false)

  const selectedValues = React.useMemo(() => {
    if (!value) return []
    return Array.isArray(value) ? value : [value]
  }, [value])

  const filteredOptions = React.useMemo(() => {
    if (!searchTerm) return GENDER_OPTIONS
    return GENDER_OPTIONS.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [searchTerm])

  const getDisplayValue = () => {
    if (!value) return placeholder

    if (multiple) {
      const values = Array.isArray(value) ? value : [value]
      if (values.length === 0) return placeholder
      if (values.length === 1) {
        const option = GENDER_OPTIONS.find(opt => opt.value === values[0])
        return option?.label || values[0]
      }
      return `${values.length} selected`
    } else {
      const singleValue = Array.isArray(value) ? value[0] : value
      const option = GENDER_OPTIONS.find(opt => opt.value === singleValue)
      return option?.label || singleValue || placeholder
    }
  }

  const handleSelect = (selectedValue: string) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : value ? [value] : []
      const newValues = currentValues.includes(selectedValue)
        ? currentValues.filter(v => v !== selectedValue)
        : currentValues.length < maxSelections
          ? [...currentValues, selectedValue]
          : currentValues
      onChange(newValues)
    } else {
      onChange(selectedValue)
      setOpen(false)
      setSearchTerm('')
    }
  }

  const handleCustomSubmit = () => {
    if (!customValue.trim()) return

    const trimmedValue = customValue.trim().toLowerCase().replace(/\s+/g, '-')

    if (multiple) {
      const currentValues = Array.isArray(value) ? value : value ? [value] : []
      if (!currentValues.includes(trimmedValue) && currentValues.length < maxSelections) {
        onChange([...currentValues, trimmedValue])
      }
    } else {
      onChange(trimmedValue)
      setOpen(false)
    }

    setCustomValue('')
    setShowCustomInput(false)
    setSearchTerm('')
  }

  const handleRemoveValue = (valueToRemove: string) => {
    if (!multiple) return
    const currentValues = Array.isArray(value) ? value : value ? [value] : []
    const newValues = currentValues.filter(v => v !== valueToRemove)
    onChange(newValues)
  }

  const clearSelection = () => {
    onChange(multiple ? [] : '')
  }

  const inputVariant = error ? 'error' : disabled ? 'disabled' : 'default'

  if (multiple) {
    return (
      <div className={cn('space-y-2', className)} {...props}>
        {label && (
          <label className={labelStyles}>
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
          <PopoverPrimitive.Trigger asChild>
            <button
              type="button"
              disabled={disabled}
              className={cn(
                baseInputStyles,
                inputVariants[inputVariant],
                'justify-between text-left',
                !value && 'text-gray-400',
                'min-h-[40px]'
              )}
              aria-haspopup="listbox"
              aria-expanded={open}
            >
              <span className="truncate">{getDisplayValue()}</span>
              <ChevronDown className={cn('ml-2 h-4 w-4 shrink-0 transition-transform duration-200', open && 'rotate-180')} />
            </button>
          </PopoverPrimitive.Trigger>
          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              className="w-[--radix-popover-trigger-width] p-0 border border-gray-300 rounded-md shadow-lg z-50 bg-white dark:bg-gray-900"
              align="start"
            >
              <div className="p-2 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                </div>
              </div>
              <div className="max-h-60 overflow-auto p-1">
                {filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className="flex items-center space-x-2 rounded-sm px-2 py-1.5 text-sm hover:bg-blue-50 cursor-pointer bg-transparent"
                    onClick={() => handleSelect(option.value)}
                  >
                    <Checkbox
                      checked={selectedValues.includes(option.value)}
                      onChange={() => handleSelect(option.value)}
                      disabled={!selectedValues.includes(option.value) && selectedValues.length >= maxSelections}
                      className="pointer-events-none"
                    />
                    <span>{option.label}</span>
                  </div>
                ))}
                {allowCustom && (
                  <>
                    {filteredOptions.length > 0 && (
                      <div className="border-t border-gray-200 my-1" />
                    )}
                    {!showCustomInput ? (
                      <div
                        className="flex items-center space-x-2 rounded-sm px-2 py-1.5 text-sm hover:bg-gray-50 cursor-pointer text-gray-600"
                        onClick={() => setShowCustomInput(true)}
                      >
                        <span>+ Add custom gender</span>
                      </div>
                    ) : (
                      <div className="p-2 space-y-2 bg-gray-50 dark:bg-gray-800">
                        <Input
                          placeholder="Enter custom gender"
                          value={customValue}
                          onChange={(e) => setCustomValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              handleCustomSubmit()
                            }
                            if (e.key === 'Escape') {
                              setShowCustomInput(false)
                              setCustomValue('')
                            }
                          }}
                          autoFocus
                          className="h-8"
                        />
                        <div className="flex justify-end space-x-2">
                          <button
                            type="button"
                            onClick={() => {
                              setShowCustomInput(false)
                              setCustomValue('')
                            }}
                            className={cn(buttonStyles, buttonVariants.ghost, 'text-xs px-2 py-1')}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleCustomSubmit}
                            disabled={!customValue.trim() || selectedValues.length >= maxSelections}
                            className={cn(buttonStyles, buttonVariants.primary, 'text-xs px-2 py-1')}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
                {filteredOptions.length === 0 && !allowCustom && (
                  <div className="py-6 text-center text-sm text-gray-500">
                    No genders found.
                  </div>
                )}
              </div>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>

        {/* Selected values display */}
        {selectedValues.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedValues.map((selectedValue) => {
              const option = GENDER_OPTIONS.find(opt => opt.value === selectedValue)
              const displayLabel = option?.label || selectedValue
              return (
                <Badge
                  key={selectedValue}
                  onRemove={() => handleRemoveValue(selectedValue)}
                >
                  {displayLabel}
                </Badge>
              )
            })}
            {showClearButton && (
              <button
                type="button"
                onClick={clearSelection}
                className={cn(buttonStyles, buttonVariants.ghost, 'text-xs px-2 py-1 text-red-600 hover:text-red-700')}
              >
                Clear all
              </button>
            )}
          </div>
        )}

        {helperText && !error && (
          <p className={helperTextStyles}>{helperText}</p>
        )}

        {error && (
          <p className={errorStyles}>{error}</p>
        )}
      </div>
    )
  }

  // Single selection mode
  return (
    <div className={cn('space-y-2', className)} {...props}>
      {label && (
        <label className={labelStyles}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <SelectPrimitive.Root
        value={Array.isArray(value) ? value[0] : value}
        onValueChange={(newValue) => onChange(newValue)}
        open={open}
        onOpenChange={setOpen}
        disabled={disabled}
      >
        <SelectPrimitive.Trigger
          className={cn(
            baseInputStyles,
            inputVariants[inputVariant],
            'justify-between text-left',
            !value && 'text-gray-400',
            'min-h-[40px]'
          )}
        >
          <SelectPrimitive.Value placeholder={placeholder} />
          <SelectPrimitive.Icon asChild>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content className="relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border border-gray-300 shadow-lg bg-white dark:bg-gray-900">
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
            </div>
            <SelectPrimitive.Viewport className="p-1">
              {filteredOptions.map((option) => (
                <SelectPrimitive.Item
                  key={option.value}
                  value={option.value}
                  className="relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-blue-50 data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                >
                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    <SelectPrimitive.ItemIndicator>
                      <Check className="h-4 w-4" />
                    </SelectPrimitive.ItemIndicator>
                  </span>
                  <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                </SelectPrimitive.Item>
              ))}
              {allowCustom && (
                <>
                  {filteredOptions.length > 0 && (
                    <div className="border-t border-gray-200 my-1" />
                  )}
                  {!showCustomInput ? (
                    <div
                      className="relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-gray-50 text-gray-600"
                      onClick={() => setShowCustomInput(true)}
                    >
                      <span>+ Add custom gender</span>
                    </div>
                  ) : (
                    <div className="p-2 space-y-2 bg-gray-50">
                      <Input
                        placeholder="Enter custom gender"
                        value={customValue}
                        onChange={(e) => setCustomValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            handleCustomSubmit()
                          }
                          if (e.key === 'Escape') {
                            setShowCustomInput(false)
                            setCustomValue('')
                          }
                        }}
                        autoFocus
                        className="h-8"
                      />
                      <div className="flex justify-end space-x-2">
                        <button
                          type="button"
                          onClick={() => {
                            setShowCustomInput(false)
                            setCustomValue('')
                          }}
                          className={cn(buttonStyles, buttonVariants.ghost, 'text-xs px-2 py-1')}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleCustomSubmit}
                          disabled={!customValue.trim()}
                          className={cn(buttonStyles, buttonVariants.primary, 'text-xs px-2 py-1')}
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
              {filteredOptions.length === 0 && !allowCustom && (
                <div className="py-6 text-center text-sm text-gray-500">
                  No genders found.
                </div>
              )}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>

      {helperText && !error && (
        <p className={helperTextStyles}>{helperText}</p>
      )}

      {error && (
        <p className={errorStyles}>{error}</p>
      )}
    </div>
  )
}